import {
  ComboBox,
  Form,
  InnerForm,
  Modal,
  Text,
  useModal,
} from "@fluidattacks/design";
import { useCallback } from "react";
import { type FC, Fragment, StrictMode } from "react";
import { useTranslation } from "react-i18next";
import { mixed, object } from "yup";

import { useRemoveVulnerabilities } from "./hooks";
import type { IRemoveVulnerabilityProps } from "./types";

export const RemoveVulnerabilityModal: FC<IRemoveVulnerabilityProps> = ({
  onClose,
  onRemoveVulnRes,
  open,
  vulnerabilities,
}): JSX.Element => {
  const { t } = useTranslation();
  const modalProps = useModal("remove-vulnerability-modal");
  const { removeVulnerabilities } = useRemoveVulnerabilities(
    vulnerabilities,
    onRemoveVulnRes,
  );

  const handleDelete = useCallback(
    async (values: { justification: string }): Promise<void> => {
      if (vulnerabilities.length > 0) {
        await removeVulnerabilities(values.justification);
      }
    },
    [vulnerabilities, removeVulnerabilities],
  );

  const validationSchema = object().shape({
    justification: mixed().required(t("validations.required")),
  });

  return (
    <StrictMode>
      <Modal
        modalRef={{ ...modalProps, close: onClose, isOpen: open }}
        size={"sm"}
        title={t("deleteVulns.title", { count: vulnerabilities.length })}
      >
        <Form
          cancelButton={{ onClick: onClose }}
          defaultValues={{ justification: "" }}
          onSubmit={handleDelete}
          yupSchema={validationSchema}
        >
          <InnerForm>
            {({ control }): JSX.Element => (
              <Fragment>
                <ComboBox
                  control={control}
                  items={[
                    {
                      name: t("searchFindings.delete.justif.duplicated"),
                      value: "DUPLICATED",
                    },
                    {
                      name: t("searchFindings.delete.justif.falsePositive"),
                      value: "FALSE_POSITIVE",
                    },
                    {
                      name: t("deleteVulns.reportingError"),
                      value: "REPORTING_ERROR",
                    },
                  ]}
                  label={t("searchFindings.delete.justif.label")}
                  name={"justification"}
                />
                <Text size={"md"}>
                  {t("deleteVulns.message", {
                    count: vulnerabilities.length,
                    vulnerabilities: vulnerabilities.length,
                  })}
                </Text>
              </Fragment>
            )}
          </InnerForm>
        </Form>
      </Modal>
    </StrictMode>
  );
};
